import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Form, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { cloneDeep, forEach, isEmpty, isEqual, map, pickBy } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';
import turf from '@turf/turf';

import * as atlasOps from '../../../api/Atlas/atlas-operations';
import * as taskOps from '../../../api/Tasks/task-operations';

import methodPromise from '../../../modules/method-promise-wrapper';
import handleErrorInClient from '../../../modules/handle-error-in-client';
import { emailRegex, filePathRegex } from '../../../modules/regex-utils';
import aiTask from '../../../modules/tasks/aiTask';

import Validation from '../../containers/Validation';

import Styles from './styles';

class AITaskConfiguration extends Component {
  state = {
    entities: {},
    selectedEntities: new Set(),
    showModal: false,
    ServiceType: ''
  };

  componentDidMount() {
    this.fetchSettings();
    this.getEntities();
  }

  componentDidUpdate(prevProps) {
    const { selectedTaskEntities } = this.props;
    const { selectedTaskEntities: prevSelectedTaskEntities } = prevProps;

    if (!isEqual(selectedTaskEntities, prevSelectedTaskEntities)) {
      this.handleEntitiesChange(selectedTaskEntities);
    }
  }

  fetchSettings = async () => {
    try {
      const AISettings = await methodPromise('admin.fetchAISetting');
      if (AISettings.length > 0) {
        this.setState({ ServiceType: AISettings[0].ServiceType });
      }
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  closeModal = () => this.setState({ showModal: false });

  getEntities = async () => {
    const { gmsConnection } = this.props;
    const entities = await methodPromise(
      'tasks.getAIEntitiesList',
      isEmpty(gmsConnection) ? '' : atlasOps.getGMSDirection(gmsConnection),
      false
    );

    this.setState({ entities });
  };

  handleEntitiesChange = selectedTaskEntities => {
    const { entities } = this.state;

    const newSelectedEntities = new Set();
    forEach(selectedTaskEntities, entity => newSelectedEntities.add(entities[entity].alias));

    this.setState({ selectedEntities: newSelectedEntities });
  };

  handleSelect = entityName =>
    this.setState(prevState => {
      const newSelectedEntities = cloneDeep(prevState.selectedEntities);
      if (!prevState.selectedEntities.has(entityName)) {
        newSelectedEntities.add(entityName);
      } else if (prevState.selectedEntities.has(entityName)) {
        newSelectedEntities.delete(entityName);
      }
      return { selectedEntities: newSelectedEntities };
    });

  handleSubmit = async form => {
    try {
      const { aiInterestArea, atlas, isPublic, gmsConnection } = this.props;
      const { entities, selectedEntities, ServiceType } = this.state;

      if (isEmpty(aiInterestArea)) throw new Error('Empty interest area');
      const bbox = `[${turf.bbox(aiInterestArea)}]`;

      const emails = map(form.emailAddress.value.split(/[;,]+/), email => email.trim());
      const emailsForTask = emails.join(' ');

      const selectedEntitiesNames = map(
        pickBy(entities, ({ alias }) => selectedEntities.has(alias)),
        (entity, key) => key
      );
      if (isEmpty(selectedEntitiesNames)) throw new Error('No entities to detect selected');

      const name = form.alias.value.trim();
      const existingTasks = await methodPromise('tasks.checkAtlasTaskName', {
        atlas,
        isPublic,
        label: taskOps.getLabel(aiTask({})),
        name
      });
      let nameForTask = name;
      if (existingTasks > 0) nameForTask += `_${existingTasks}`;

      const folder =
        ServiceType === 'LOCAL'
          ? map(form.folder.value.split(/[;,]+/), config => config.trim())
          : '';
      const folderPath = ServiceType === 'LOCAL' ? folder.join('') : '';

      const ftpParameters = await methodPromise('tasks.getTaskFTPParameters');

      const taskToRun = aiTask({
        bbox,
        emails: emailsForTask,
        entities: selectedEntitiesNames,
        ftpParameters,
        isOptimized: form.isOptimized.checked,
        owner: atlasOps.getOwner(atlas),
        taskName: nameForTask,
        folderPath
      });
      await methodPromise('tasks.runTask', {
        authenticate: false,
        debugIP: isEmpty(gmsConnection) ? '' : atlasOps.getGMSDirection(gmsConnection),
        isTasker: false,
        itemForOwner: { id: atlasOps.getId(atlas), collectionName: 'atlas' },
        taskToRun
      });

      this.setState({ showModal: false });
      Bert.alert(`Task launched with alias ${nameForTask}`, 'info');
    } catch (error) {
      handleErrorInClient(error);
    }
  };

  openModal = () => this.setState({ showModal: true });

  render() {
    const { isPreview, isPublic } = this.props;
    const { entities, selectedEntities, showModal, ServiceType } = this.state;

    const validationProps = {
      rules: {
        alias: {
          required: true
        },
        emailAddress: {
          required: true,
          multiemails: emailRegex
        },
        folder: {
          required: true,
          format: filePathRegex
        }
      },
      messages: {
        alias: {
          required: "What's the task alias?"
        },
        emailAddress: {
          required: 'Need an email address here.',
          multiemails: 'Are all these email addresses correct?'
        },
        folder: {
          required: "What's the task path?"
        }
      },
      submitHandler: form => {
        this.handleSubmit(form);
      }
    };

    return (
      <Styles.AITaskConfigurationContainer>
        <Modal show={showModal} onHide={this.closeModal}>
          <Modal.Header closeButton>
            <Modal.Title>Introduce your data</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Validation {...validationProps}>
              <Form onSubmit={event => event.preventDefault()}>
                <Form.Row>
                  <Form.Group as={Col} xs={12}>
                    <Form.Label>Alias</Form.Label>
                    <Form.Control type="text" name="alias" defaultValue="" />
                  </Form.Group>
                  <Form.Group as={Col} xs={12}>
                    <Form.Label>Email Addresses</Form.Label>
                    <Form.Control type="text" name="emailAddress" defaultValue="" />
                  </Form.Group>
                  {ServiceType === 'LOCAL' && (
                    <Form.Group as={Col} xs={12}>
                      <Form.Label>Folder</Form.Label>
                      <Form.Control type="text" name="folder" defaultValue="" />
                    </Form.Group>
                  )}
                </Form.Row>
                <Form.Group>
                  <Form.Label>Models</Form.Label>
                  <Form.Control as="select" name="dataset" id="size">
                    <option value="SkyNet">SkyNet</option>
                    <option value="Model trained">Model trained</option>
                  </Form.Control>
                </Form.Group>
                <Form.Row>
                  <Form.Check type="switch" label="Optimized features" id="isOptimized" />
                </Form.Row>
                <Form.Row>
                  <Col xs={{ span: 4, offset: 4 }} className="pt-3">
                    <Button block type="submit" variant="success">
                      Run Detection
                    </Button>
                  </Col>
                </Form.Row>
              </Form>
            </Validation>
          </Modal.Body>
        </Modal>
        <Form.Row>
          <Form.Label column="lg" xs={12} className="mb-0 mx-1 px-3">
            Select features
          </Form.Label>
        </Form.Row>
        <Row
          className="overflow-auto mx-3 d-flex flex-row align-items-start"
          style={{ height: 'calc(100% - 100px)' }}
        >
          {map(entities, ({ alias, icon }) => (
            <Col key={alias} xs={3} md={4} xl={3} className="m-0 p-0 border rounded-lg">
              <OverlayTrigger placement="bottom" overlay={<Tooltip>{alias}</Tooltip>}>
                <Button
                  block
                  className="p-0"
                  disabled={isPreview}
                  variant={selectedEntities.has(alias) ? 'primary' : 'Light'}
                  onClick={() => this.handleSelect(alias)}
                >
                  <img src={icon} alt="" width="100%" />
                </Button>
              </OverlayTrigger>
            </Col>
          ))}
        </Row>
        <Row className="mb-3 pb-3">
          <Col
            xs={{ span: 4, offset: 4 }}
            sm={{ span: isPublic ? 4 : 6, offset: isPublic ? 4 : 3 }}
            md={{ span: isPublic ? 8 : 10, offset: isPublic ? 2 : 1 }}
            lg={{ span: isPublic ? 6 : 8, offset: isPublic ? 3 : 2 }}
            xl={{ span: 6, offset: 3 }}
          >
            <Button block disabled={isPreview} variant="primary" onClick={this.openModal}>
              Configuration
            </Button>
          </Col>
        </Row>
      </Styles.AITaskConfigurationContainer>
    );
  }
}

AITaskConfiguration.propTypes = {
  aiInterestArea: PropTypes.object.isRequired,
  atlas: PropTypes.object.isRequired,
  gmsConnection: PropTypes.object.isRequired,
  isPreview: PropTypes.bool.isRequired,
  isPublic: PropTypes.bool.isRequired,
  selectedTaskEntities: PropTypes.array.isRequired
};

export default AITaskConfiguration;
